import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useOrg } from '../app/OrgProvider';
import { AppLink } from '../components/Nav';
import {
  Badge,
  Banner,
  Button,
  CopyButton,
  EmptyState,
  ErrorText,
  Input,
  Loading,
  PageHeader,
  Spinner,
} from '../components/ui';
import { errorMessage } from '../lib/api';
import type { BrandingSettings } from '../lib/types';
import { qk } from '../queries/keys';
import {
  checkCustomDomain,
  domainInFlight,
  domainLabel,
  fetchBranding,
  removeCustomDomain,
  setCustomDomain,
} from '../queries/branding';
import { SettingsTabs } from './SettingsPage';

/**
 * Settings › Domain. Serving a team's share links from their own address.
 *
 * Its own tab rather than a section under Branding: the rest of branding is a
 * handful of choices somebody makes in a sitting, and this is a walk with
 * waiting in it, done once, usually by whoever looks after DNS rather than
 * whoever picked the logo. It also reads the same settings object, so opening
 * this tab after that one costs nothing.
 */

/**
 * Three stages, one record at a time. Which record is shown follows the
 * status, because showing both at once means the second is stale for as long
 * as the first takes.
 */
function DomainSection({
  branding,
  orgId,
  disabled,
  onSettled,
}: {
  branding: BrandingSettings;
  orgId: string;
  disabled: boolean;
  onSettled: (next: BrandingSettings) => void;
}) {
  const domain = branding.domain;
  const [host, setHost] = useState('');
  const label = domainLabel(domain.status);

  const claim = useMutation({
    mutationFn: () => setCustomDomain(orgId, host.trim()),
    onSuccess: onSettled,
  });
  const check = useMutation({ mutationFn: () => checkCustomDomain(orgId), onSuccess: onSettled });
  const drop = useMutation({
    mutationFn: () => removeCustomDomain(orgId),
    onSuccess: (next) => {
      setHost('');
      onSettled(next);
    },
  });

  // While a domain is in flight the page asks again every fifteen seconds.
  // The API throttles what it actually asks AWS, so this is cheap, and it is
  // what makes the walk finish without anybody pressing anything.
  useEffect(() => {
    if (!domainInFlight(domain.status)) return;
    const timer = window.setInterval(() => {
      if (!check.isPending) check.mutate();
    }, 15_000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain.status, check.isPending]);

  if (!domain.available) {
    return (
      <p className="text-caption text-chalk-faint">
        Custom domains are not available on this deployment.
      </p>
    );
  }

  if (!domain.host) {
    return (
      <form
        className="max-w-md space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (host.trim()) claim.mutate();
        }}
      >
        <Input
          label="Domain"
          placeholder="videos.acme.com"
          value={host}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => setHost(event.target.value)}
          hint="A subdomain of a domain you own. You will add one DNS record to prove it is yours, and a second to point it at us."
        />
        <Button type="submit" variant="primary" size="sm" busy={claim.isPending} disabled={disabled || !host.trim()}>
          Add domain
        </Button>
        {claim.isError ? <ErrorText>{errorMessage(claim.error)}</ErrorText> : null}
      </form>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-mono text-body text-chalk">{domain.host}</span>
        <Badge tone={label.tone}>{label.title}</Badge>
        {domainInFlight(domain.status) ? <Spinner size={14} className="text-chalk-faint" /> : null}
      </div>

      {domain.status === 'active' ? (
        <Banner tone="go" title="Links use your domain">
          New links look like <span className="font-mono">https://{domain.host}/v/…</span>. Links already sent on the
          Slate address keep working.
        </Banner>
      ) : null}

      {domain.error ? <Banner tone="caution" title="Last check">{domain.error}</Banner> : null}

      {domain.record ? (
        <div className="rounded-md border border-hairline bg-slate-900/40 p-3">
          <p className="text-caption text-chalk-dim">
            {domain.status === 'verifying'
              ? 'Add this record at your DNS provider. It proves the domain is yours so a certificate can be issued; it usually takes a few minutes once the record is live.'
              : 'Now point the domain itself at us with this record. Links switch over on their own once it resolves.'}
          </p>
          <dl className="mt-3 space-y-2">
            {[
              { label: 'Type', value: domain.record.type },
              { label: 'Name', value: domain.record.name },
              { label: 'Value', value: domain.record.value },
            ].map((row) => (
              <div key={row.label} className="flex items-start gap-3">
                <dt className="w-14 shrink-0 pt-1 text-caption text-chalk-faint">{row.label}</dt>
                <dd className="flex min-w-0 flex-1 items-start gap-2">
                  <code className="min-w-0 flex-1 break-all rounded bg-slate-950 px-2 py-1 font-mono text-caption text-chalk">
                    {row.value}
                  </code>
                  <CopyButton value={row.value} label="Copy" copiedLabel="Copied" size="sm" />
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}

      <div className="flex items-center gap-3">
        <Button size="sm" busy={check.isPending} disabled={disabled} onClick={() => check.mutate()}>
          Check again
        </Button>
        <button
          type="button"
          className="text-caption text-chalk-dim hover:text-chalk disabled:opacity-40"
          disabled={disabled || drop.isPending}
          onClick={() => drop.mutate()}
        >
          {drop.isPending ? 'Removing…' : 'Remove domain'}
        </button>
      </div>
      {check.isError ? <ErrorText>{errorMessage(check.error)}</ErrorText> : null}
      {drop.isError ? <ErrorText>{errorMessage(drop.error)}</ErrorText> : null}
    </div>
  );
}

export function DomainPage() {
  const { org, loading: orgLoading } = useOrg();
  const client = useQueryClient();
  const orgId = org?.id ?? '';

  const query = useQuery({
    queryKey: qk.branding(orgId),
    queryFn: () => fetchBranding(orgId),
    enabled: orgId !== '',
  });
  const branding = query.data ?? null;
  const settled = (next: BrandingSettings) => client.setQueryData(qk.branding(orgId), next);

  if (orgLoading || (orgId !== '' && query.isLoading)) {
    return (
      <>
        <PageHeader title="Settings" />
        <SettingsTabs active="domain" />
        <Loading label="Loading your domain" />
      </>
    );
  }

  if (!org) {
    return (
      <>
        <PageHeader title="Settings" subtitle="Your account, your plan and your team." />
        <SettingsTabs active="domain" />
        <EmptyState title="A domain belongs to a team">
          Share links on your own address come with the team plan, so there has to be a team first.
          <div className="mt-4">
            <AppLink to="/settings/subscription">
              <Button variant="primary">Start a team</Button>
            </AppLink>
          </div>
        </EmptyState>
      </>
    );
  }

  if (query.isError || !branding) {
    return (
      <>
        <PageHeader title="Settings" />
        <SettingsTabs active="domain" />
        <Banner tone="danger" title="That did not load">{errorMessage(query.error)}</Banner>
      </>
    );
  }

  const locked = !branding.mayManage || !branding.included;

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle={`Where ${org.name}’s share links live.`}
      />
      <SettingsTabs active="domain" />

      <div className="space-y-6">
        {!branding.included ? (
          <Banner
            tone="caution"
            title="A custom domain comes with the team plan"
            action={
              <AppLink to="/settings/subscription">
                <Button size="sm" variant="primary">
                  See the plan
                </Button>
              </AppLink>
            }
          >
            Links are served from the Slate address until the plan is running again.
          </Banner>
        ) : !branding.mayManage ? (
          <Banner tone="info" title="Only admins and owners can change this">
            This is where {org.name}’s share links are served from.
          </Banner>
        ) : null}

        <section className="card p-4 sm:p-5">
          <h2 className="text-h4 text-chalk">Custom domain</h2>
          <p className="mt-1 max-w-xl text-caption text-chalk-dim">
            Serve your links from your own address, so a recording you send reads as yours from the link onwards.
            Links already sent on the Slate address keep working, whatever you do here.
          </p>
          <div className="mt-4">
            <DomainSection branding={branding} orgId={org.id} disabled={locked} onSettled={settled} />
          </div>
        </section>
      </div>
    </>
  );
}
